import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Chip } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NearMeIcon from '@mui/icons-material/NearMe';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import DoNotStepIcon from '@mui/icons-material/DoNotStep';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import DirectionsIcon from '@mui/icons-material/Directions';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  KOTTAYIL_DISTANCE_REFERENCE,
  KOTTAYIL_NEARBY_TEMPLE_DEFAULTS,
  mergeWithKottayilTempleDefaults,
} from '../../data/nearbyTemplesDefaults';

const GOLD = '#d4af37';
const TEMPLE_IMAGE_DOC_REF_PREFIX = 'temple-image-doc:';

interface TempleItem {
  id: string;
  name: string;
  distance: string;
  deity: string;
  location: string;
  description?: string;
  color?: string;
  imageUrl?: string;
  mapUrl?: string;
  lat?: number;
  lng?: number;
}

interface TemplesContent {
  title: string;
  description: string;
  temples: TempleItem[];
}

const gradients: [string, string][] = [
  ['#ef4444', '#dc2626'],
  ['#f59e0b', '#d97706'],
  ['#8b5cf6', '#7c3aed'],
  ['#22c55e', '#16a34a'],
  ['#3b82f6', '#2563eb'],
  ['#ec4899', '#db2777'],
  ['#06b6d4', '#0891b2'],
];

const templeLat = 11.4806;
const templeLng = 75.5916;

const getTempleMapsUrl = (temple: TempleItem) => {
  if (temple.mapUrl && temple.mapUrl.trim().length > 0) {
    return temple.mapUrl;
  }
  if (typeof temple.lat === 'number' && typeof temple.lng === 'number') {
    return `https://www.google.com/maps/dir/?api=1&origin=${templeLat},${templeLng}&destination=${temple.lat},${temple.lng}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${temple.name}, ${temple.location}`)}`;
};

const tips = [
  { text: 'Wear modest clothing that covers shoulders & knees', icon: CheckroomIcon },
  { text: 'Remove footwear before entering temple premises', icon: DoNotStepIcon },
  { text: 'Photography may not be allowed inside sanctum', icon: NoPhotographyIcon },
  { text: 'Maintain silence and decorum inside the temple', icon: VolumeOffIcon },
  { text: 'Check festival calendar for special darshan timings', icon: CalendarMonthIcon },
  { text: 'Some temples have specific visiting hours', icon: AccessTimeIcon },
];

const isTempleImageDocRef = (value?: string) =>
  typeof value === 'string' && value.startsWith(TEMPLE_IMAGE_DOC_REF_PREFIX);

const extractTempleImageDocId = (value?: string) => {
  if (!isTempleImageDocRef(value)) return '';
  return value.slice(TEMPLE_IMAGE_DOC_REF_PREFIX.length).trim();
};

export default function Temples() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<TemplesContent>({
    title: 'Temples Nearby',
    description: `Curated temple circuit around Muchukunnu. ${KOTTAYIL_DISTANCE_REFERENCE}.`,
    temples: KOTTAYIL_NEARBY_TEMPLE_DEFAULTS,
  });

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'nearby_content', 'temples'), (docSnap) => {
        void (async () => {
          if (docSnap.exists()) {
            const data = docSnap.data() as TemplesContent;
            const currentTemples = mergeWithKottayilTempleDefaults(
              Array.isArray(data.temples) ? data.temples : []
            );
            const hydratedTemples = await Promise.all(
              currentTemples.map(async (temple) => {
                const imageDocId = extractTempleImageDocId(temple.imageUrl);
                if (!imageDocId) return temple;
                try {
                  const imageSnap = await getDoc(doc(db, 'nearby_content', imageDocId));
                  if (!imageSnap.exists()) {
                    return { ...temple, imageUrl: '' };
                  }
                  const imageData = imageSnap.data()?.imageData;
                  if (typeof imageData === 'string' && imageData.length > 0) {
                    return { ...temple, imageUrl: imageData };
                  }
                  return { ...temple, imageUrl: '' };
                } catch (error) {
                  console.error(`Failed to load temple image doc: ${imageDocId}`, error);
                  return { ...temple, imageUrl: '' };
                }
              })
            );

            setContent({
              title: data.title || 'Temples Nearby',
              description: data.description || `Curated temple circuit around Muchukunnu. ${KOTTAYIL_DISTANCE_REFERENCE}.`,
              temples: hydratedTemples,
            });
          } else {
            setContent({
              title: 'Temples Nearby',
              description: `Curated temple circuit around Muchukunnu. ${KOTTAYIL_DISTANCE_REFERENCE}.`,
              temples: KOTTAYIL_NEARBY_TEMPLE_DEFAULTS,
            });
          }
          setLoading(false);
        })();
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading temples:', error);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  const items = content.temples.map((t, i) => ({ ...t, gradient: gradients[i % gradients.length] }));

  return (
    <Box sx={{ bgcolor: '#f7f4ef', minHeight: '100vh' }}>
      <SEO title="Temples Near Kottayil Temple | സമീപ ക്ഷേത്രങ്ങൾ" description="Notable temples near Kottayil Temple (Kotta-Kovilakam Kshethram) in Kozhikode, Kerala. Discover ancient temples and sacred places to visit nearby." path="/nearby/temples" keywords="temples near Kottayil temple, Kozhikode temples, Kerala temples, temples to visit, കോഴിക്കോട് ക്ഷേത്രങ്ങൾ, സമീപ ക്ഷേത്രങ്ങൾ, nearby temples Muchukunnu" />
      <PageBanner title="Temples Nearby" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>
            <Box
              sx={{
                borderRadius: '30px',
                overflow: 'hidden',
                mb: 4,
                boxShadow: '0 22px 64px rgba(80, 20, 8, 0.18)',
                background: 'linear-gradient(145deg, #5e2016 0%, #8a2e1f 48%, #bd4b2a 100%)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.13,
                  background:
                    'repeating-linear-gradient(120deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 2px, transparent 2px, transparent 14px)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: -56,
                  right: -56,
                  width: 240,
                  height: 240,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -48,
                  left: -48,
                  width: 190,
                  height: 190,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 72%)',
                }}
              />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      width: 66,
                      height: 66,
                      borderRadius: '20px',
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(255,255,255,0.25)',
                      boxShadow: '0 12px 30px rgba(48,14,6,0.25)',
                    }}
                  >
                    <TempleHinduIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: '1.6rem', sm: '2rem' },
                        color: '#fff',
                        lineHeight: 1.15,
                        mb: 0.5,
                        textShadow: '0 2px 12px rgba(0,0,0,0.2)',
                      }}
                    >
                      Sacred Temples Nearby
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.82rem', sm: '0.9rem' },
                        color: 'rgba(255,255,255,0.65)',
                        fontWeight: 500,
                      }}
                    >
                      Pilgrimage circuit around Kottayil Shiva Temple
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: '0.88rem', sm: '0.95rem' },
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.9,
                    maxWidth: 560,
                    mb: 2.5,
                  }}
                >
                  {content.description || `Curated temple circuit around Muchukunnu. ${KOTTAYIL_DISTANCE_REFERENCE}.`}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.2 }}>
                  <Chip
                    icon={<TempleHinduIcon sx={{ fontSize: '16px !important', color: '#a63c22 !important' }} />}
                    label={`${items.length} Temple${items.length !== 1 ? 's' : ''}`}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.95)',
                      color: '#7a2916',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      height: 34,
                      borderRadius: '12px',
                      '& .MuiChip-icon': { ml: '8px' },
                    }}
                  />
                  <Chip
                    icon={<TravelExploreIcon sx={{ fontSize: '16px !important', color: '#fbbf24 !important' }} />}
                    label={KOTTAYIL_DISTANCE_REFERENCE}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.14)',
                      color: 'rgba(255,255,255,0.92)',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      height: 34,
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.18)',
                      '& .MuiChip-icon': { ml: '8px' },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' },
                    gap: 1.3,
                  }}
                >
                  {[
                    { label: 'Temple Directory', value: `${items.length}`, icon: TempleHinduIcon },
                    { label: 'Spiritual Focus', value: 'Traditional', icon: SelfImprovementIcon },
                    { label: 'Travel Ready', value: 'From Kottayil Base', icon: DirectionsIcon },
                  ].map((metric) => {
                    const MIcon = metric.icon;
                    return (
                      <Box
                        key={metric.label}
                        sx={{
                          borderRadius: '14px',
                          p: 1.4,
                          bgcolor: 'rgba(255,255,255,0.13)',
                          border: '1px solid rgba(255,255,255,0.16)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '9px',
                              bgcolor: 'rgba(255,255,255,0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <MIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.2 }}>
                              {metric.value}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.7rem', lineHeight: 1.2 }}>
                              {metric.label}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {items.length === 0 ? (
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '24px',
                  p: 6,
                  textAlign: 'center',
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                }}
              >
                <TempleHinduIcon sx={{ fontSize: 56, color: '#d1c3b3', mb: 2 }} />
                <Typography sx={{ fontSize: { xs: '0.88rem', md: '1rem' }, color: '#8d7f71', fontWeight: 600 }}>
                  Temple directory coming soon.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ mb: 2.2 }}>
                  <Chip
                    icon={<NearMeIcon sx={{ fontSize: '14px !important', color: '#8a2e1f !important' }} />}
                    label={`${KOTTAYIL_DISTANCE_REFERENCE} (editable in Admin > Nearby > Temples)`}
                    sx={{
                      bgcolor: 'rgba(138,46,31,0.08)',
                      color: '#8a2e1f',
                      fontWeight: 700,
                      fontSize: '0.76rem',
                      borderRadius: '10px',
                      border: '1px solid rgba(138,46,31,0.15)',
                      '& .MuiChip-icon': { ml: '8px' },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' },
                    gap: 2.5,
                    mb: 4,
                  }}
                >
                {items.map((t) => {
                  const [gStart, gEnd] = t.gradient;
                  const mapsUrl = getTempleMapsUrl(t);
                  return (
                    <Box
                      key={t.id || t.name}
                      sx={{
                        bgcolor: '#fff',
                        borderRadius: '24px',
                        overflow: 'hidden',
                        border: '1px solid rgba(101,59,29,0.12)',
                        boxShadow: '0 4px 24px rgba(84,48,26,0.08)',
                        transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: `0 20px 60px rgba(84,48,26,0.2), 0 0 0 1px ${gStart}30`,
                        },
                        '&:hover .temple-card-img': {
                          transform: 'scale(1.06)',
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        {t.imageUrl ? (
                          <Box sx={{ width: '100%', height: { xs: 200, sm: 240 }, position: 'relative', overflow: 'hidden' }}>
                            <Box
                              component="img"
                              src={t.imageUrl}
                              alt={t.name}
                              className="temple-card-img"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 600ms ease',
                              }}
                            />
                            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.1) 100%)' }} />
                            <Box sx={{ position: 'absolute', bottom: 14, left: 14, right: 14, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                              <Box
                                sx={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: '13px',
                                  background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: `0 6px 20px ${gStart}55`,
                                  flexShrink: 0,
                                }}
                              >
                                <TempleHinduIcon sx={{ fontSize: 22, color: '#fff' }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.02rem', sm: '1.12rem' }, color: '#fff', lineHeight: 1.2, textShadow: '0 2px 10px rgba(0,0,0,0.28)' }}>
                                  {t.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.84)', fontWeight: 500 }}>
                                  {t.location}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                              px: { xs: 2.5, sm: 3 },
                              py: { xs: 2, sm: 2.5 },
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -15,
                                right: -15,
                                width: 90,
                                height: 90,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.12)',
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: -25,
                                left: -18,
                                width: 70,
                                height: 70,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.08)',
                              }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                              <Box
                                sx={{
                                  width: 46,
                                  height: 46,
                                  borderRadius: '14px',
                                  background: 'rgba(255,255,255,0.2)',
                                  backdropFilter: 'blur(10px)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid rgba(255,255,255,0.25)',
                                }}
                              >
                                <TempleHinduIcon sx={{ fontSize: 24, color: '#fff' }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '1.05rem', sm: '1.15rem' },
                                    color: '#fff',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {t.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                                  {t.location}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, bgcolor: '#fffaf5' }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.7, flexWrap: 'wrap' }}>
                          <Chip
                            component="a"
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable
                            icon={<NearMeIcon sx={{ fontSize: '15px !important', color: `${gStart} !important` }} />}
                            label={t.distance}
                            size="small"
                            sx={{
                              bgcolor: `${gStart}10`,
                              color: gStart,
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              height: 30,
                              borderRadius: '10px',
                              border: `1px solid ${gStart}20`,
                              textDecoration: 'none',
                              cursor: 'pointer',
                              '& .MuiChip-icon': { ml: '6px' },
                              '&:hover': { bgcolor: `${gStart}20` },
                            }}
                          />
                          {t.deity && (
                            <Chip
                              icon={<AutoAwesomeIcon sx={{ fontSize: '14px !important', color: `${GOLD} !important` }} />}
                              label={t.deity}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(212,175,55,0.08)',
                                color: GOLD,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                height: 30,
                                borderRadius: '10px',
                                border: '1px solid rgba(212,175,55,0.15)',
                                '& .MuiChip-icon': { ml: '6px' },
                              }}
                            />
                          )}
                          <Chip
                            icon={<LocationOnIcon sx={{ fontSize: '14px !important', color: '#64748b !important' }} />}
                            label={t.location}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(100,116,139,0.08)',
                              color: '#64748b',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 30,
                              borderRadius: '10px',
                              border: '1px solid rgba(100,116,139,0.12)',
                              '& .MuiChip-icon': { ml: '6px' },
                            }}
                          />
                        </Box>
                        {t.description && (
                          <Typography
                            sx={{
                              fontSize: '0.86rem',
                              color: '#6e5b4c',
                              lineHeight: 1.8,
                              letterSpacing: '0.01em',
                              minHeight: 74,
                              mb: 2.2,
                            }}
                          >
                            {t.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                background: 'linear-gradient(145deg, #332015 0%, #513122 45%, #26170f 100%)',
                borderRadius: '24px',
                p: { xs: 3, sm: 3.5 },
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.05)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.06,
                  backgroundImage: 'linear-gradient(115deg, rgba(255,255,255,0.15) 0 2px, transparent 2px 12px)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)`,
                }}
              />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${GOLD}, #b8941f)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 6px 20px ${GOLD}30`,
                    }}
                  >
                    <TipsAndUpdatesIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>
                      Temple Visit Etiquette
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>
                      Respect sacred traditions
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={1.5}>
                  {tips.map((t, i) => {
                    const TIcon = t.icon;
                    return (
                      <Grid item xs={12} sm={6} key={i}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1,
                            px: 1.5,
                            borderRadius: '10px',
                            transition: 'all 250ms',
                            border: '1px solid rgba(255,255,255,0.06)',
                            bgcolor: 'rgba(255,255,255,0.02)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                          }}
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '7px',
                              bgcolor: `${GOLD}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <TIcon sx={{ fontSize: 13, color: GOLD }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.6 }}>
                            {t.text}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="nearby" activePath="/nearby/temples" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
